import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Edit,
  ExternalLink,
  Globe,
  MapPin,
  Users,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { EmpresaDetailSkeleton } from "@/components/empresas/EmpresaDetailSkeleton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EmpresaEditModal } from "@/components/empresas/EmpresaEditModal";
import { ExternalLinkButton } from "@/components/ui/external-link-button";

interface Empresa {
  id: string;
  nome: string;
  slug: string | null;
  segmento: string | null;
  tipo: string | null;
  cidade: string | null;
  pais: string | null;
  website: string | null;
  linkedin_url: string | null;
  descricao: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface AgenciaVinculada {
  id: string;
  nome: string;
}

interface ExperienciaComFreelancer {
  id: string;
  freelancer_id: string;
  empresa_nome_raw: string;
  cargo_nome_raw: string;
  data_inicio: string;
  data_fim: string | null;
  is_atual: boolean;
  duracao_meses: number | null;
  localizacao: string | null;
  freelancers: { id: string; nome: string; email: string | null } | null;
}

const PAGE_SIZE = 50;

export default function EmpresaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [page, setPage] = useState(0);

  const empresaQuery = useQuery({
    queryKey: ["empresa", id],
    queryFn: async () => {
      if (!id) throw new Error("ID inválido");
      const { data, error } = await supabase
        .from("empresas" as never)
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as Empresa) ?? null;
    },
    enabled: !!id,
  });

  const agenciasQuery = useQuery({
    queryKey: ["empresa-agencias", id],
    queryFn: async () => {
      if (!id) return [];
      const { data, error } = await supabase
        .from("agencias" as never)
        .select("id, nome")
        .eq("empresa_id", id)
        .order("nome");
      if (error) throw error;
      return (data as unknown as AgenciaVinculada[]) ?? [];
    },
    enabled: !!id,
  });

  const experienciasQuery = useQuery({
    queryKey: ["empresa-experiencias", id, page],
    queryFn: async () => {
      if (!id) return { rows: [], count: 0 };
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error, count } = await supabase
        .from("freelancer_experiencias" as never)
        .select(
          "id, freelancer_id, empresa_nome_raw, cargo_nome_raw, data_inicio, data_fim, is_atual, duracao_meses, localizacao, freelancers(id, nome, email)",
          { count: "exact" },
        )
        .eq("empresa_id", id)
        .order("data_inicio", { ascending: false })
        .range(from, to);
      if (error) throw error;
      return {
        rows: (data as unknown as ExperienciaComFreelancer[]) ?? [],
        count: count ?? 0,
      };
    },
    enabled: !!id,
  });

  // Métricas agregadas (total, tenure médio/mediano) — calculadas com base em
  // todas as experiências da empresa, não só a página atual. Para empresas
  // muito populares, isto pode ficar pesado; pra começar, é simples e correto.
  const metricsQuery = useQuery({
    queryKey: ["empresa-metrics", id],
    queryFn: async () => {
      if (!id) return { totalFreelancers: 0, tenureMedio: null, tenureMediano: null };
      const { data, error } = await supabase
        .from("freelancer_experiencias" as never)
        .select("freelancer_id, duracao_meses")
        .eq("empresa_id", id);
      if (error) throw error;
      const rows = (data as unknown as { freelancer_id: string; duracao_meses: number | null }[]) ?? [];
      const totalFreelancers = new Set(rows.map((r) => r.freelancer_id)).size;
      const durations = rows
        .map((r) => r.duracao_meses)
        .filter((v): v is number => typeof v === "number" && v > 0);
      const tenureMedio = durations.length
        ? durations.reduce((a, b) => a + b, 0) / durations.length
        : null;
      const sorted = [...durations].sort((a, b) => a - b);
      const tenureMediano = sorted.length
        ? sorted.length % 2 === 1
          ? sorted[(sorted.length - 1) / 2]
          : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : null;
      return { totalFreelancers, tenureMedio, tenureMediano };
    },
    enabled: !!id,
  });

  const empresa = empresaQuery.data;
  const isLoading = empresaQuery.isLoading;

  const totalExperiencias = experienciasQuery.data?.count ?? 0;
  const hasMore = useMemo(
    () => totalExperiencias > (page + 1) * PAGE_SIZE,
    [totalExperiencias, page],
  );

  if (isLoading) {
    return <EmpresaDetailSkeleton />;
  }
  if (empresaQuery.isError || !empresa) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/empresas")} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        {empresaQuery.isError ? (
          <ErrorState onRetry={() => empresaQuery.refetch()} />
        ) : (
          <EmptyState
            icon={Building2}
            title="Empresa não encontrada"
            description="Ela pode ter sido removida ou o link está incorreto."
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/empresas")} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-2">
          <Edit className="h-4 w-4" /> Editar
        </Button>
      </div>

      {/* Header */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold tracking-tight">{empresa.nome}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {empresa.tipo && <Badge variant="outline">{empresa.tipo}</Badge>}
                {empresa.segmento && (
                  <Badge className="bg-purple-100 text-purple-800">{empresa.segmento}</Badge>
                )}
                {(empresa.cidade || empresa.pais) && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {[empresa.cidade, empresa.pais].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              {empresa.descricao && (
                <p className="mt-3 text-sm whitespace-pre-wrap">{empresa.descricao}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                {empresa.website && (
                  <a
                    href={empresa.website}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Globe className="h-3.5 w-3.5" /> Website
                  </a>
                )}
                <ExternalLinkButton
                  kind="linkedin"
                  url={empresa.linkedin_url}
                  variant="inline"
                  label="LinkedIn"
                  className="text-sm text-primary hover:underline"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agência vinculada */}
      {(agenciasQuery.data ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Agência vinculada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(agenciasQuery.data ?? []).map((a) => (
                <Badge key={a.id} variant="outline" className="bg-blue-50 text-blue-800">
                  <Briefcase className="h-3 w-3 mr-1" />
                  {a.nome}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Freelancers que passaram pela empresa */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Freelancers que trabalharam aqui
          </CardTitle>
          {metricsQuery.data && metricsQuery.data.totalFreelancers > 0 && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground mt-1">
              <span>
                <strong className="text-foreground">
                  {metricsQuery.data.totalFreelancers}
                </strong>{" "}
                freelancer{metricsQuery.data.totalFreelancers === 1 ? "" : "s"}
              </span>
              {metricsQuery.data.tenureMedio !== null && (
                <span>
                  Tenure médio:{" "}
                  <strong className="text-foreground">
                    {formatDuration(metricsQuery.data.tenureMedio)}
                  </strong>
                </span>
              )}
              {metricsQuery.data.tenureMediano !== null && (
                <span>
                  Mediana:{" "}
                  <strong className="text-foreground">
                    {formatDuration(metricsQuery.data.tenureMediano)}
                  </strong>
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {experienciasQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          ) : experienciasQuery.isError ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>Não foi possível carregar os freelancers.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => experienciasQuery.refetch()}
              >
                Tentar novamente
              </Button>
            </div>
          ) : (experienciasQuery.data?.rows ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum freelancer registrado para esta empresa ainda.
            </p>
          ) : (
            <div className="space-y-2">
              {(experienciasQuery.data?.rows ?? []).map((exp) => (
                <div
                  key={exp.id}
                  className="flex items-start justify-between gap-3 border rounded p-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    {exp.freelancers ? (
                      <Link
                        to={`/freelancers/${exp.freelancers.id}`}
                        className="font-medium hover:underline inline-flex items-center gap-1"
                      >
                        {exp.freelancers.nome}
                        <ExternalLink className="h-3 w-3 opacity-60" />
                      </Link>
                    ) : (
                      <span className="font-medium text-muted-foreground">Freelancer removido</span>
                    )}
                    {exp.cargo_nome_raw && (
                      <div className="text-sm text-muted-foreground">{exp.cargo_nome_raw}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatPeriodo(exp.data_inicio, exp.data_fim, exp.is_atual)}
                      {exp.duracao_meses !== null && (
                        <span className="ml-2">· {formatDuration(exp.duracao_meses)}</span>
                      )}
                    </div>
                    {exp.localizacao && (
                      <div className="text-xs text-muted-foreground">{exp.localizacao}</div>
                    )}
                  </div>
                  {exp.is_atual && (
                    <Badge variant="outline" className="text-xs shrink-0">
                      atual
                    </Badge>
                  )}
                </div>
              ))}
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={experienciasQuery.isFetching}
                >
                  Ver mais ({totalExperiencias - (page + 1) * PAGE_SIZE} restantes)
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <EmpresaEditModal
        empresa={editing ? (empresa as never) : null}
        onClose={() => setEditing(false)}
        onSaved={() => {
          toast({ title: "Empresa atualizada" });
          queryClient.invalidateQueries({ queryKey: ["empresa", id] });
          queryClient.invalidateQueries({ queryKey: ["empresas-list"] });
          setEditing(false);
        }}
      />
    </div>
  );
}

function formatDuration(months: number): string {
  if (months <= 0) return "-";
  const y = Math.floor(months / 12);
  const m = Math.round(months % 12);
  if (y === 0) return `${m} ${m === 1 ? "mês" : "meses"}`;
  if (m === 0) return `${y} ${y === 1 ? "ano" : "anos"}`;
  return `${y}a ${m}m`;
}

function formatPeriodo(inicio: string, fim: string | null, atual: boolean): string {
  const start = formatYearMonth(inicio);
  const end = atual ? "presente" : fim ? formatYearMonth(fim) : "?";
  return `${start} – ${end}`;
}

function formatYearMonth(s: string): string {
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (!m) return s;
  const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${meses[parseInt(m[2], 10) - 1]}/${m[1]}`;
}
